export type LoggedInParticipantType =
  | "EXHIBITOR"
  | "SPEAKER"
  | "VISITOR"
  | "SPONSOR"
  | "MEDIA"
  | "SUPPORT"
  | "USER"
  | "OTHER";

export type LoggedInUserTypeSuccess = {
  type: LoggedInParticipantType;
  id: string;
};

export type LoggedInUserTypeResult =
  | LoggedInUserTypeSuccess
  | { error: string };

export type CompanyHit = { id: string } | null | false | undefined;

/**
 * Same priority as the web login helper. Differs from `determineEntityType`
 * by returning OTHER instead of falling through to VISITOR.
 */
export function typeFromGroups(
  types: readonly string[]
): LoggedInParticipantType | null {
  if (!types.length) return null;
  if (types.includes("EXHIBITORS")) return "EXHIBITOR";
  if (types.includes("SPEAKERS")) return "SPEAKER";
  if (types.includes("SPONSORS")) return "SPONSOR";
  if (types.includes("MEDIA")) return "MEDIA";
  if (types.includes("SUPPORT")) return "SUPPORT";
  if (types.includes("OTHER")) return "OTHER";
  if (types.includes("VISITORS")) return "VISITOR";
  return null;
}

function companyId(hit: CompanyHit): string | null {
  return hit && typeof hit === "object" && hit.id ? hit.id : null;
}

export function resolveLoggedInUserTypeDecision(input: {
  userId?: string | null;
  groupTypes: readonly string[];
  exhibitorByEmail: CompanyHit;
  sponsorByEmail: CompanyHit;
  teamExhibitor: CompanyHit;
  teamSponsor: CompanyHit;
}): LoggedInUserTypeResult {
  const userId = input.userId ?? undefined;
  const type = typeFromGroups(input.groupTypes);

  if (type) {
    if (type === "EXHIBITOR" || type === "SPONSOR") {
      const resolvedCompanyId =
        type === "EXHIBITOR"
          ? companyId(input.exhibitorByEmail) ?? companyId(input.teamExhibitor)
          : companyId(input.sponsorByEmail) ?? companyId(input.teamSponsor);
      if (!resolvedCompanyId) {
        if (!userId) {
          return { error: "Participant account not found for this event" };
        }
        return { type: "VISITOR", id: userId };
      }
      return { type, id: resolvedCompanyId };
    }
    if (!userId) {
      return { error: "Participant account not found for this event" };
    }
    return { type, id: userId };
  }

  const exhibitorId = companyId(input.exhibitorByEmail);
  if (exhibitorId) {
    return { type: "EXHIBITOR", id: exhibitorId };
  }
  const sponsorId = companyId(input.sponsorByEmail);
  if (sponsorId) {
    return { type: "SPONSOR", id: sponsorId };
  }
  const teamExhibitorId = companyId(input.teamExhibitor);
  if (teamExhibitorId) {
    return { type: "EXHIBITOR", id: teamExhibitorId };
  }
  const teamSponsorId = companyId(input.teamSponsor);
  if (teamSponsorId) {
    return { type: "SPONSOR", id: teamSponsorId };
  }

  if (userId) {
    return { type: "VISITOR", id: userId };
  }

  return { error: "Participant account not found for this event" };
}
